import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Divider, Grid, lighten, TextField, Typography } from "@mui/material";
import { Page, Table } from "../../components";
import { AdminTable } from "../../components/table/AdminTable";
import { ICompanySegmentationsAPI } from "../../fetch/company-client-segmentations";
import { ISegment, ProductType, Segment } from "../../models/company-client-segmentations";
import { Add } from "@mui/icons-material"
import { useToastContext } from '../../hooks';
import { ConfirmationDialogue } from "../../components/modal/ConfirmationDialogue";
import { AddProductTypeModal } from "./AddProductTypeModal";
import { ClientTypesDropDown } from "./ClientTypesDropDown";
import { makeStyles } from "@mui/styles";

interface StateProps{
  isLoading: boolean;
  productTypes: ProductType[];
  selectedClientType: number|null;
  isMobileFilterButtonOpen: boolean;
  activeProductTab: ProductType | null;
  currentSegments: Segment[];
  clientTypes: any[];
  newProductType: {name: string;description:string;error:string;};
  showModal: boolean;
  savingProduct: boolean;
  openConfirmationModal: boolean;
  segmentToDelete: Segment|null;
  clientTypeChanged:boolean;
}

const useStyles = makeStyles(() => ({
  dropdownContainer: {
    padding: '0 0.5rem',
  },
  divider:{
    margin: '0.5rem 0.5rem',
  },
  mainContent: {
    padding: '0.5rem 0.5rem',
  },
  addProductTypeButton: {
    minWidth: '12rem', 
    marginBottom: "0.5rem"
  }
}));

export const ClientSegmentation: FC = () => {
    const { showToast } = useToastContext();
    const classes = useStyles();
    const [state, setState] = useState<StateProps>({
      isLoading: true,
      productTypes: [],
      selectedClientType: null,
      isMobileFilterButtonOpen: false,
      activeProductTab: null,
      currentSegments: [],
      clientTypes: [],
      newProductType: {name:"", description: "", error: ""},
      showModal: false,
      savingProduct: false,
      openConfirmationModal: false,
      segmentToDelete: null,
      clientTypeChanged: false,
    });
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const saveSegments = async () => {
      setIsSaving(true);
      const rows = state.currentSegments;
      let {productTypes, activeProductTab, currentSegments} = state;
      try {
        const result = await ICompanySegmentationsAPI.addUpdateSegments(rows);
        currentSegments = result?.data || currentSegments;
        productTypes = productTypes.map(x => {
          if(x.productTypeId === activeProductTab?.productTypeId){
            x.segmentations = currentSegments;
          }
          return x;
        });
        setHasChanges(false);
        showToast('success', "Success saving segments");
      } catch (_error) {
        showToast('error', "Error saving segments");
      } finally {
        setIsSaving(false);
        setState(prev => ({
          ...prev,
          currentSegments,
          productTypes,
        }));
      }
    };

    const handleAddProduct = async() => {
      let {selectedClientType, productTypes, showModal} = state;
      let {name, description, error} = state.newProductType; 
      if(name.length > 1 && selectedClientType !== null){
        setState(prev => ({...prev, savingProduct: true}));
        try {
          const detail:ProductType = {
            name,
            description,
            clientTypeId: selectedClientType
          };
          const response = await ICompanySegmentationsAPI.addUpdateProductTypes(detail);
          productTypes = [...productTypes, response?.data];
          name = "";
          description = "";
          showModal = false;
          showToast("success", "Success saving product type");
        } catch (_error) {
          error = "Error saving product type";
        }
        finally{
            setState(prev => ({
              ...prev,
              productTypes,
              savingProduct: false,
              showModal,
              newProductType: {name, description, error}
            }))
        }
      }
    };

    const handleDeleteSegment = async() => {
      let {currentSegments, segmentToDelete} = state;
      if(!segmentToDelete)return;
      if(!(segmentToDelete.id > 0)){
        currentSegments = currentSegments.filter(x => x.id !== segmentToDelete?.id);
        setState(prev => ({...prev, openConfirmationModal: false, currentSegments}))  
        return;              
      }
      try {
        setState(prev => ({...prev, isLoading: false}));
        await ICompanySegmentationsAPI.deleteSegment(segmentToDelete.id);
        currentSegments = currentSegments.filter(x => x.id !== segmentToDelete?.id);
        showToast('success', "Segment deleted");
      } catch (error) {
        showToast("error", "Error deleting segment");
      }
      finally{
        setState(prev => ({
          ...prev,
          currentSegments,
          openConfirmationModal: false,
          isLoading: false
        }))
      }
    };
    const confirmDeleteSegment = (segmentToDelete: Segment) => {
      setState(prev => ({
        ...prev,
        openConfirmationModal: true,
        segmentToDelete,
      }))
    }

    const closeModal = () => {
      setState(prev => ({
        ...prev,
        showModal: false,
        newProductType: {name: "", description: "", error: ""}
      }));
    };

    const addRow = () => {
      setState(prev => ({
      ...prev,
      currentSegments: [
        {
          ...ISegment.getDefault(), 
          productTypeId: prev.activeProductTab?.productTypeId
        }, 
        ...prev.currentSegments,
      ]
    }))};


    const getCurrentSegmentsByProductId = useCallback((id: number|undefined ) => {
      if(!id) return [];
      return state.productTypes.filter((x: any) => x.productTypeId === id)[0]?.segmentations;
    },[state.productTypes])

    const updateParentState = (update: {}) => {
      setState(prev => ({
        ...prev,
        ...update
      }));
    };

    
    useEffect(() => {
      if(state.clientTypeChanged || !state.selectedClientType){
        (async() => {
          let {clientTypes, selectedClientType, currentSegments, productTypes,activeProductTab} = state;
          try {
            if(!selectedClientType){
              clientTypes = await ICompanySegmentationsAPI.getCLientTypes();
              selectedClientType = clientTypes[0].clientTypeId;
            }
            const result = await ICompanySegmentationsAPI.getCompanyClientSegments(selectedClientType||clientTypes[0].clientTypeId);
            
            currentSegments=[...result?.data.filter((x: any) => x.productTypeId === result?.data[0]?.productTypeId)[0]?.segmentations || []];
            productTypes= result?.data;
            activeProductTab= result?.data[0]; 
          } catch (error) {
            showToast('error', "Error fetching data")
          }
          finally{
            setState(prev => ({
              ...prev,
              clientTypes,
              selectedClientType,
              currentSegments,
              productTypes,
              activeProductTab,
              clientTypeChanged: false,
              isLoading: false
            }));
          }
        })();
      };
    },[state.selectedClientType]);//eslint-disable-line react-hooks/exhaustive-deps

    const columns = useMemo(() => {
      return [
        {
          accesor: 'name',
          Header: 'Segment',
          verticalAlignment: 'top',
          sort: false,
          Cell: ({
            cell: {
              row: { original, index },
            },
          }: {
            cell: { row: { original: Segment; index: number } };
          }) => {
            const [segmentName, setSegmentName] = useState(original.name); 
            return (
              <TextField
                fullWidth
                inputProps={{
                  className: 'segment-name-input',
                  id: `segment-name-input-${index}`
                }}
                placeholder="Segment Name"
                value={segmentName}
                onChange={({ target: { value } }) => setSegmentName(value)}
                onBlur={() => {
                  const accountIndex = state.currentSegments.indexOf(original);
                  const segments = state.currentSegments;
                  // Enable save button
                  if (segments[accountIndex].name !== segmentName) {
                    segments[accountIndex].isEdited = true;
                    setHasChanges(true);
                  }
                  // Store changes in preparation for batch save
                  segments[accountIndex].name = segmentName;
                  setState(prev => {
                    return {
                      ...prev, 
                      currentSegments: [...segments],
                    };
                  });
                }}
              />              
            );
          },
        },
        {
          accesor: 'segmentType',
          Header: 'Segment Type',
          verticalAlignment: 'top',
          sort: false,
          Cell: ({
            cell: {
              row: { original, index },
            },
          }: {
            cell: { row: { original: Segment; index: number } };
          }) => {
            const [segmentType, setSegmentType] = useState(original.segmentType); 
            return (
              <TextField
                fullWidth
                inputProps={{
                  id: `segment-type-input-${index}`,
                  className: 'segment-type-input',
                }}
                placeholder="Segment Type"
                value={segmentType}
                onChange={({ target: { value } }) => setSegmentType(value)}
                onBlur={() => {
                  const accountIndex = state.currentSegments.indexOf(original);
                  const segments = state.currentSegments;
                  // Enable save button
                  if (segments[accountIndex].segmentType !== segmentType) {
                    segments[accountIndex].isEdited = true;
                    setHasChanges(true);
                  }
                  // Store changes in preparation for batch save
                  segments[accountIndex].segmentType = segmentType;
                  setState(prev => {
                    return {
                      ...prev, 
                      currentSegments: [...segments],
                    };
                  });
                }}
              /> 
            );
          },
        },
        {
          accesor: 'description',
          Header: 'Description',
          verticalAlignment: 'top',
          sort: false,
          Cell: ({
            cell: {
              row: { original, index },
            },
          }: {
            cell: { row: { original: Segment; index: number } };
          }) => {
            const [description, setDescription] = useState(original.description);  
            return (
              <TextField
                fullWidth
                inputProps={{
                  id: `description-input-${index}`,
                  className: 'description-input',
                }}
                placeholder="Description"
                value={description}
                onChange={({ target: { value } }) => setDescription(value)}
                onBlur={() => {
                  const accountIndex = state.currentSegments.indexOf(original);
                  const segments = state.currentSegments;
                  // Enable save button
                  if (segments[accountIndex].description !== description) {
                    segments[accountIndex].isEdited = true;
                    setHasChanges(true);
                  }
                  // Store changes in preparation for batch save
                  segments[accountIndex].description = description;
                  setState(prev => {
                    return {
                      ...prev, 
                      currentSegments:[...segments],
                    };
                  });
                }}
              /> 
            );
          },
        },                
      ]}, [state.currentSegments]);//eslint-disable-line react-hooks/exhaustive-deps

    const productTypes = useMemo(() => {
      return [
        {
          id: 'header',
          accessor: '',
          Header: 'Company Client Segmentations',
          disableSortBy: true,
          sort: false,
          Cell: ({
            cell: {
              row: { original },
            },
          }: {
            cell: { row: { original: ProductType } };
          }) => {
            const row = original;
            return (
              <Box>
                <Grid
                  item
                  key={row.productTypeId}
                  sx={theme => ({
                    transition: 'padding 160ms ease-in',
                    borderRadius: 1,
                    padding: theme.spacing(0.465),
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: lighten(theme.frankie.neonYellow, 0.8),
                      paddingInline: theme.spacing(1),
                    },
                    // Display Frankie-themed background if this is the selected table.
                    ...(state.activeProductTab?.productTypeId === row.productTypeId
                      ? {
                          backgroundColor: lighten(theme.frankie.neonYellow, 0.6),
                          borderLeft: '5px solid',
                          borderColor: theme.frankie.neonYellow,
                          paddingInline: theme.spacing(1),
                          '.MuiTypography-root': {
                            fontWeight: '700',
                          },
                        }
                      : {}),
                  })}
                  onClick={() => {
                    setState((prev) => ({
                      ...prev,
                      activeProductTab: row,
                      currentSegments: getCurrentSegmentsByProductId(row?.productTypeId) || []
                    }));
                  }}
                >
                  <Typography
                    sx={theme => ({
                      color: theme.frankie.searchBlue,
                    })}
                  >
                    {row.name}
                  </Typography>
                </Grid>
              </Box>
            );
          },
        },
      ];
    }, [state.activeProductTab, getCurrentSegmentsByProductId]);    

    return (
      <Page title="Company Client Segmentations">
        {/* Confirmation of Delete */}
        <ConfirmationDialogue 
          id="delete-segment-confirmation"
          title="Delete selected segment"
          open={state.openConfirmationModal}
          onConfirm={handleDeleteSegment}
          onClose={() => {
            setState(prev => ({...prev,
            openConfirmationModal: false,
          }))}}
        />
      <AddProductTypeModal
        showModal={state.showModal}
        closeModal={closeModal}
        updateParentState={updateParentState}
        newProductType={state.newProductType}
        handleAddProduct={handleAddProduct}
        savingProduct = {state.savingProduct}
       />
      <Grid container className={classes.dropdownContainer} direction="row" justifyContent="space-between">
        <Grid item xs="auto">
          <ClientTypesDropDown
            isLoading={state.isLoading}
            clientTypes={state.clientTypes}
            selectedClientType={state.selectedClientType}
            updateParentState={updateParentState}
           />
        </Grid>
        </Grid>   
        <Divider className={classes.divider} />     
      <Grid container direction="row" justifyContent="space-between" className={classes.mainContent} gap={2}>
        <Grid item xs="auto" container direction="column">
        <Grid item xs="auto">
            <Button 
            disabled={!state.selectedClientType}
            variant="outlined" 
            className={classes.addProductTypeButton}
            color="secondary" 
            endIcon={<Add />}            
            onClick={() => {
              setState(prev => ({
                ...prev,
                showModal: true
              }))
            }}
            >Add Product Type</Button>
          </Grid>          
        <Grid 
          item 
          xs="auto"
          sx={theme => ({
            minWidth: '12rem',
            borderRightStyle: 'solid',
            boxSizing: 'border-box',
            borderWidth: '2px',
            borderColor: theme.frankie.secondary,
            flexGrow: 1,
            '.MuiTableCell-root': {
              paddingRight: '0',
            },
        })}
        >
          <Table
            data={state.productTypes}
            columns={productTypes}
            isLoading={state.isLoading}
            hidePagination
            hideTableHeader={true}
          /> 
        </Grid>

        </Grid>
        <Grid item xs>
          <AdminTable
            data={state.currentSegments}
            isLoading={state.isLoading}
            columns={columns}
            isSaving={isSaving}
            hasChanges={hasChanges && !!state.activeProductTab?.productTypeId}
            onSave={saveSegments}
            onAdd={addRow}
            onDelete={async(original, _index) => confirmDeleteSegment(original)}
           />
        </Grid>
      </Grid>
      </Page>
    );
};

